/**
 * Dashboard Slice (Redux Toolkit)
 *
 * Cross-view shared state for pipeline runs, events, and metrics.
 * All views subscribe to this slice so data correlates across the dashboard.
 *
 * RTK's createSlice uses Immer under the hood — you can write "mutating"
 * syntax (e.g., state.value = 1) and Immer produces an immutable update.
 * This is the same mechanism as Zustand's immer middleware, but built-in.
 *
 * Key difference from Zustand: state and actions are separated.
 * Zustand merges them into one interface; RTK keeps state in `initialState`
 * and actions in `reducers`. Action creators are auto-generated by createSlice.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { EventLogEntry } from "../types/metrics";
import type { PipelineState } from "../types/pipeline";

/** Maximum events stored — FIFO eviction when exceeded */
const MAX_EVENTS = 200;

/** State shape: only data, no action functions (unlike Zustand's combined interface) */
interface DashboardState {
  /** Current pipeline run state (shared across Overview, Pipeline, Security, Traces) */
  pipelineState: PipelineState | null;
  isRunning: boolean;

  /** Event log (Overview event stream, Security timeline, Traces correlation) */
  events: EventLogEntry[];

  /** Selected trace for cross-view correlation (Traces ↔ EventStream) */
  selectedTraceId: string | null;
}

const initialState: DashboardState = {
  pipelineState: null,
  isRunning: false,
  events: [],
  selectedTraceId: null,
};

/**
 * createSlice auto-generates action creators from reducer names.
 * e.g., `reducers: { addEvent }` → exports `dashboardSlice.actions.addEvent`
 *
 * Each reducer receives (state, action) where:
 * - `state` is an Immer draft (safe to "mutate")
 * - `action.payload` is typed by PayloadAction<T>
 */
export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setPipelineState(state, action: PayloadAction<PipelineState | null>) {
      state.pipelineState = action.payload;
    },

    setIsRunning(state, action: PayloadAction<boolean>) {
      state.isRunning = action.payload;
    },

    /**
     * Prepend a single event (newest first). Cap at MAX_EVENTS.
     *
     * Immer note: `unshift` mutates the draft array in place — Immer
     * detects this and produces a new immutable array under the hood.
     * We still need `splice` for truncation because Immer proxies
     * don't support assigning to `.length` for array truncation.
     */
    addEvent(state, action: PayloadAction<EventLogEntry>) {
      state.events.unshift(action.payload);
      if (state.events.length > MAX_EVENTS) {
        state.events.splice(MAX_EVENTS);
      }
    },

    /** Batch-prepend multiple events. Cap at MAX_EVENTS. */
    addEvents(state, action: PayloadAction<EventLogEntry[]>) {
      state.events.unshift(...action.payload);
      if (state.events.length > MAX_EVENTS) {
        state.events.splice(MAX_EVENTS);
      }
    },

    clearPipeline(state) {
      state.pipelineState = null;
      state.isRunning = false;
    },

    setSelectedTraceId(state, action: PayloadAction<string | null>) {
      state.selectedTraceId = action.payload;
    },
  },
});

export const {
  setPipelineState,
  setIsRunning,
  addEvent,
  addEvents,
  clearPipeline,
  setSelectedTraceId,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
